import _ from 'lodash';
import React, {memo, useCallback} from 'react';

import {LoaderProxy} from '../../containers/AppLoader';
import {isInJupyterNotebook} from '../../setup';
import {PanelBankContextProvider} from '../../state/panelbank/context';
import {PanelSearchContextProvider} from '../../state/panelbank/PanelSearchContextProvider';
import {usePart} from '../../state/views/hooks';
import {DispatchableAction} from '../../types/redux';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {useAutoSyncPanelBankContext} from './AutoSyncPanelBankContext';
import {PanelBankSectionsOpenTempProvider} from './hooks/panelBankSectionIsOpenHooks';
import {PanelBankConfigWithRefsProvider} from './hooks/usePanelBankConfigWithRefs';
import {PanelBankVisibleSectionsProvider} from './hooks/useVisibleSectionsContext';
import {PanelBankLayout} from './PanelBankLayout';
// @ts-ignore TODO How to get this type in without breaking weave?
import {produceSearchHistory} from './produceSearchHistory';
import type {BaseSearchHistoryItem, PanelBankProps} from './types';

export const PanelBank = memo(function PanelBank(props: PanelBankProps) {
  const {panelBankConfigRef, panelBankActions} = useWorkspaceRefsContext();
  const panelBankConfigPart = usePart(panelBankConfigRef);

  // I think using debounce is a good practice here, but eslint can't check the dependencies
  // if you use a function call instead of inline code, so disabling the lint error.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveDebouncedSearch = useCallback(
    // we debouce so that these changes don't cause the view to be constantly saved while the user is typing
    // We use a longer wait time than debouncedSearchQuery, and it makes sense that the interval for
    // local searching would be shorter than that for saving to the server, so it's on purpose that
    // those are different
    _.debounce(debouncedSearchQuery => {
      panelBankActions.setCurrentSearch(debouncedSearchQuery);
    }, 1000),
    [panelBankActions.setCurrentSearch]
  );

  const addToHistory = useAddToHistory(
    panelBankActions.updateSearchHistory,
    panelBankConfigPart.settings.searchHistory
  );

  const {isLoading} = useAutoSyncPanelBankContext();

  return (
    <PanelBankContextProvider>
      <PanelSearchContextProvider
        savedSearch={panelBankConfigPart.settings.searchQuery}
        onDebouncedSearch={saveDebouncedSearch}
        onAddToHistory={addToHistory}
        searchHistory={panelBankConfigPart.settings.searchHistory ?? []}
        shouldAutoFocus={!isInJupyterNotebook()}>
        {isLoading ? (
          <LoaderProxy name="use-auto-sync-panel-bank-loader" />
        ) : (
          <PanelBankConfigWithRefsProvider>
            <PanelBankVisibleSectionsProvider>
              <PanelBankSectionsOpenTempProvider>
                <PanelBankLayout {...props} />
              </PanelBankSectionsOpenTempProvider>
            </PanelBankVisibleSectionsProvider>
          </PanelBankConfigWithRefsProvider>
        )}
      </PanelSearchContextProvider>
    </PanelBankContextProvider>
  );
});

export const useAddToHistory = <T extends BaseSearchHistoryItem>(
  updateSearchHistory: (searchHistory: T[] | undefined) => DispatchableAction,
  currentSearchHistory: T[] | undefined
) => {
  return useCallback(
    (newQuery: string, isRegex?: boolean) => {
      const searchHistory = produceSearchHistory(
        newQuery,
        currentSearchHistory,
        undefined,
        isRegex
      );
      if (!searchHistory) {
        return;
      }
      updateSearchHistory(searchHistory);
    },
    [updateSearchHistory, currentSearchHistory]
  );
};
